import { Button, Chip, Dialog, FormControlLabel, IconButton, MenuItem, Select, TextField, Typography } from '@mui/material'
import { Box } from '@mui/system'
import { CloseIcon } from '../../../assets/icons'
import { useState } from 'react'
import { LeadRequiredColumnValues, PipelineStagesOutput } from '../../../types/procedureOutputs'
import MoveLeadsService from '../../../services/MoveLeadService'
import { trpc } from '../../../core/trpc'
import { useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import { RequiredFields, TermLength } from '@prisma/client'
import { DatePicker } from '@mui/x-date-pickers'
import { omit } from 'remeda'
import { Decimal } from 'decimal.js'
import StyledCheckbox from '../../../components/StyledCheckbox'
import { StageChip } from '../../../components/Chips'
import { leadTypeOptions } from '../../../consts/lead'
import LeadOwnerAutoCompleteField from '../../../components/LeadOwnerAutoCompleteField'

type props = {
  closeModal: () => void
  stage: PipelineStagesOutput[0]['stages'][0] | undefined
  columnValues: LeadRequiredColumnValues
}

const ValidateModal = ({ closeModal, stage, columnValues }: props) => {
  const [isContractNotRequired, setContractNotRequired] = useState(false)
  const queryClient = useQueryClient()
  const updateRequiredFieldsMutation = trpc.lead.updateLeadRequiredFields.useMutation({
    onSuccess: () => {
      queryClient.refetchQueries([['offer', 'getOffers'], { input: { leadId: columnValues.leadId, search: '', skip: 0, take: 10 }, type: 'query' }])
      toast.success('Lead successfully updated')
    },
    onError: () => toast.error('Lead update failed')
  })

  const client = useQueryClient()
  const [mapper, setMapper] = useState<LeadRequiredColumnValues['mapper']>(columnValues.mapper)

  const mutation = trpc.lead.updateLead.useMutation({
    onSuccess: () => {
      client.refetchQueries([['lead', 'getLeadById'], { input: { id: columnValues.leadId }, type: 'query' }])
      client.refetchQueries([
        ['activity', 'getActivitiesByLeadId'],
        { input: { activityTypes: [], leadId: columnValues.leadId, skip: 0, take: 10 }, type: 'query' }
      ])
      toast.success('Successfully updated stage')
      closeModal()
    },
    onError: (e) => {
      toast.error(e?.message ?? 'Stage update failed')
    }
  })

  const handleSubmitValue = async () => {
    if (mapper !== columnValues.mapper) {
      await updateRequiredFieldsMutation.mutateAsync({
        leadId: columnValues.leadId,
        declinedOfferId: columnValues.deniedOfferId,
        offerId: columnValues.offerId,
        mapper: omit(
          {
            ...mapper,
            DATE_FUNDED: mapper['DATE_FUNDED'] ? new Date(mapper['DATE_FUNDED']) : undefined,
            FOLLOW_UP_DATE: mapper['FOLLOW_UP_DATE'] ? new Date(mapper['FOLLOW_UP_DATE']) : undefined,
            BUY_RATE: mapper['BUY_RATE'] ? new Decimal(mapper['BUY_RATE']) : undefined,
            COMMISSION_POINTS: mapper['COMMISSION_POINTS'] ? new Decimal(mapper['COMMISSION_POINTS']) : undefined,
            FUNDED_AMOUNT: mapper['FUNDED_AMOUNT'] ? new Decimal(mapper['FUNDED_AMOUNT']) : undefined,
            SERVICE_FEE: mapper['SERVICE_FEE'] ? new Decimal(mapper['SERVICE_FEE']) : undefined,
            FINAL_PURCHASE_PRICE: mapper['FINAL_PURCHASE_PRICE'] ? new Decimal(mapper['FINAL_PURCHASE_PRICE']) : undefined,
            FINAL_PURCHASE_AMOUNT: mapper['FINAL_PURCHASE_AMOUNT'] ? new Decimal(mapper['FINAL_PURCHASE_AMOUNT']) : undefined
          },
          ['FUNDER_NAME', 'OFFER_STATUS_APPROVED', 'CONTRACT_FILE']
        )
      })
    }
    mutation.mutate({ id: columnValues.leadId || '', stage_id: stage?.id, contract_file_not_required: isContractNotRequired })
  }

  const renderCorrectInput = (rf: RequiredFields) => {
    const numberFields: RequiredFields[] = ['TERM_AMOUNT']
    const decimalFields: RequiredFields[] = [
      'COMMISSION_POINTS',
      'BUY_RATE',
      'FUNDED_AMOUNT',
      'SERVICE_FEE',
      'FINAL_PURCHASE_PRICE',
      'FINAL_PURCHASE_AMOUNT'
    ]

    if (rf === 'DATE_FUNDED' || rf === 'FOLLOW_UP_DATE') {
      return (
        <DatePicker
          slotProps={{ textField: { fullWidth: true, error: false } }}
          value={new Date(mapper[rf] ?? '') ?? null}
          onChange={(value: Date | null) => setMapper({ ...mapper, [rf]: value as Date })}
        />
      )
    } else if (rf === 'LEAD_TYPE') {
      return (
        <Select
          error={mapper[rf] === null || mapper[rf] === undefined}
          fullWidth
          value={mapper['LEAD_TYPE'] || ''}
          onChange={(e) => setMapper({ ...mapper, LEAD_TYPE: e.target.value as (typeof leadTypeOptions)[number] })}
        >
          {leadTypeOptions.map((s) => (
            <MenuItem key={s} value={s}>
              {s}
            </MenuItem>
          ))}
        </Select>
      )
    } else if (rf === 'LEAD_OWNER') {
      return <LeadOwnerAutoCompleteField leadOwnerId={mapper[rf] || ''} onChange={(newId) => setMapper({ ...mapper, [rf]: newId })} />
    } else if (rf === 'TERM_LENGTH') {
      return (
        <Select
          error={mapper[rf] === null || mapper[rf] === undefined}
          fullWidth
          value={mapper['TERM_LENGTH']}
          onChange={(e) => setMapper({ ...mapper, TERM_LENGTH: e.target.value as TermLength })}
        >
          {Object.values(TermLength).map((s) => (
            <MenuItem key={s} value={s}>
              {s}
            </MenuItem>
          ))}
        </Select>
      )
    } else if (numberFields.includes(rf)) {
      return (
        <TextField
          className="mt-1"
          fullWidth
          error={mapper[rf] === null || mapper[rf] === undefined}
          value={mapper[rf]}
          type={'number'}
          onChange={(e) => setMapper({ ...mapper, [rf]: +e.target.value })}
        />
      )
    } else if (decimalFields.includes(rf)) {
      return (
        <TextField
          className="mt-1"
          fullWidth
          error={mapper[rf] === null || mapper[rf] === undefined}
          value={mapper[rf]}
          type={'number'}
          onChange={(e) => setMapper({ ...mapper, [rf]: e.target.value })}
        />
      )
    }

    return (
      <TextField
        className="mt-1"
        fullWidth
        error={mapper[rf] === null || mapper[rf] === undefined}
        value={mapper[rf]}
        onChange={(e) => setMapper({ ...mapper, [rf]: e.target.value })}
      />
    )
  }

  if (!stage) {
    return <></>
  }

  const funderColumns: RequiredFields[] = [
    'COMMISSION_POINTS',
    'DATE_FUNDED',
    'BUY_RATE',
    'FUNDED_AMOUNT',
    'FUNDER_NAME',
    'OFFER_STATUS_APPROVED',
    'TERM_AMOUNT',
    'TERM_LENGTH',
    'SERVICE_FEE',
    'FINAL_PURCHASE_PRICE',
    'FINAL_PURCHASE_AMOUNT',
    'CONTRACT_FILE'
  ]

  const missingOffer = stage.required_fields.some((r) => {
    return (
      (r === 'OFFER_STATUS_APPROVED' && mapper['OFFER_STATUS_APPROVED'] === false) ||
      (r === 'FUNDER_NAME' && mapper['FUNDER_NAME'] === undefined) ||
      (!columnValues.offerId && funderColumns.includes(r)) ||
      (!columnValues.deniedOfferId && r === 'OFFER_REASON_FOR_DECLINE') ||
      (r === 'OFFER_STATUS_ACCEPTED' && mapper['OFFER_STATUS_ACCEPTED'] === false)
    )
  })

  const isMissingContractFile = stage.required_fields.includes('CONTRACT_FILE') && !mapper.CONTRACT_FILE

  const accepted = stage.required_fields.some((r) => r === 'OFFER_STATUS_ACCEPTED')
  return (
    <Dialog open={true}>
      <Box className="p-5 min-w-[540px]">
        <Box className="flex justify-between items-start mb-5">
          <Typography className="text-grey-900 text_lg_semibold">Confirm Required Information</Typography>
          <IconButton onClick={() => closeModal()}>
            <CloseIcon fontSize="small" className="text-grey-500" />
          </IconButton>
        </Box>
        <Box className="flex gap-4 items-center mb-6">
          <Typography className="text-grey-900 text_lg_semibold">Stage</Typography>
          <StageChip color={stage.color} stageId={stage.id} stageName={stage.name} textColor={stage.text_color} />
          {columnValues?.mapper?.FUNDER_NAME && stage.required_fields.some((f) => funderColumns.includes(f)) && (
            <Chip label={`Deal: ${columnValues?.mapper?.FUNDER_NAME}`} />
          )}
        </Box>
        {isMissingContractFile && (
          <>
            <Typography className="text-error-500">Missing contract file</Typography>
            <FormControlLabel
              onChange={(_, checked) => setContractNotRequired(checked)}
              checked={isContractNotRequired}
              label="Contract file not required"
              control={<StyledCheckbox />}
            />
          </>
        )}
        {missingOffer ? (
          <Typography className="text-error-500">
            There are no {accepted ? 'accepted' : 'approved'} offers yet, please {accepted ? 'accept' : 'approve'} one and then try to change the
            stage again
          </Typography>
        ) : (
          stage.required_fields
            .filter((rf) => rf !== 'OFFER_STATUS_APPROVED' && rf !== 'FUNDER_NAME' && rf !== 'OFFER_STATUS_ACCEPTED' && rf !== 'CONTRACT_FILE')
            .map((rf) => (
              <Box key={rf} className="mb-4">
                <Typography> {MoveLeadsService.getRequiredFieldDisplayName(rf)}</Typography>
                {renderCorrectInput(rf)}
                {mapper[rf] === null || mapper[rf] === undefined ? (
                  <Typography className="text-error-500">Required to update stage</Typography>
                ) : undefined}
              </Box>
            ))
        )}
        <Box className="flex justify-end gap-3">
          <Button
            variant="outlined"
            onClick={() => {
              closeModal()
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => handleSubmitValue()}
            disabled={stage.required_fields.some((rf) => {
              if (rf === 'CONTRACT_FILE' && isContractNotRequired) return false
              return mapper[rf] === undefined || mapper[rf] === null
            })}
          >
            Update Stage
          </Button>
        </Box>
      </Box>
    </Dialog>
  )
}
export default ValidateModal
